'use client';

import { RingGauge, Row, Stack, Text } from '../../atoms';
import { SlChart } from '../../charts';
import { cn } from '../../lib/cn';
import {
	BalanceLine,
	BalanceList,
	BodySkeleton,
	CalcTrace,
	ChartSkeleton,
	DashGrid,
	DashPanel,
	DashSection,
	ExplodeButton,
	KpiBig,
	KpiSkeleton,
	ListSkeleton,
	MemberRowItem,
	PanelCard,
	PanelCardSkeleton,
	RingSkeleton,
	SegmentedTabs,
	StatusBlock,
	TONE_VAR,
	type CalcTraceData,
	type WidgetTone,
} from '../../molecules';
import { PageTitle } from '../../templates';
import { type ReactNode, useMemo } from 'react';
import { fmtNum, fmtPct } from '@slims/shared/calculations';
import type {
	CERConfig,
	DashboardHourlyKpi,
	DashboardKpiSummary,
	DashboardMemberKpi,
	EnergyMember,
} from '@slims/shared/types';
import { MEMBER_TYPE_LABELS, stimaNote } from '@slims/shared/types';

/** La nota della «i» con in coda quanta parte del valore è stimata (INB-354). */
function withStima(note: string, share: number): string {
	const stima = stimaNote(share);
	return stima ? `${note} ${stima}` : note;
}

export type DashboardViewConfig = Omit<CERConfig, 'id' | 'tenantId'> & {
	id?: string;
	tenantId?: string;
};

/**
 * Vista attiva della Dashboard: `multi` è la griglia «Panoramica» (una card per
 * configurazione), `single` è il pannello completo di una configurazione sola.
 */
export type DashboardViewMode = 'multi' | 'single';

/** Voce del picker di configurazione, in modalità singola. */
export interface DashboardConfigPickerItem {
	id: string;
	cerNome: string;
	configNome: string;
}

export interface DashboardPageViewProps {
	/** Contenuto del pannello «Configurazione singola» — una {@link DashboardPanelView}. */
	content: ReactNode;
	/**
	 * Contenuto della griglia «Panoramica»: una {@link DashboardConfigCard} per
	 * configurazione, fornita dal wiring — ognuna fa il proprio fetch di dati,
	 * la vista non sa come si ottengono.
	 */
	gridSlot?: ReactNode;
	/**
	 * Configurazioni disponibili per il picker di modalità singola. Assente (o
	 * vuoto) insieme a `view`/`onViewChange`: niente toggle, si mostra solo
	 * `content` — il comportamento di prima di questa vista.
	 */
	configs?: DashboardConfigPickerItem[];
	/** Vista attiva. */
	view?: DashboardViewMode;
	onViewChange?: (view: DashboardViewMode) => void;
	/** Configurazione scelta nel picker, in modalità singola. */
	selectedConfigId?: string | null;
	onSelectConfig?: (id: string) => void;
	/**
	 * Finestra temporale della griglia «Panoramica» — un solo selettore per
	 * tutte le card, non uno a testa: è la stessa finestra che il wiring passa
	 * a ciascuna per farne il fetch.
	 */
	multiPeriod?: DashboardViewPeriod;
	onMultiPeriodChange?: (period: DashboardViewPeriod) => void;
}

export interface DashboardPanelViewProps {
	config: DashboardViewConfig;
	members: EnergyMember[];
	energyTotals: {
		totalImmessa: number;
		totalPrelevata: number;
		totalCondivisa: number;
	};
	summary: DashboardKpiSummary | null;
	/** Quante raccomandazioni AI sono state seguite nel mese corrente (carichi spostati). */
	aiActionsCount?: number | null;
	showAiSuggestions?: boolean;
	/**
	 * Apre l'esploso per POD di un pannello. La vista non sa cosa sia un POD né
	 * come si scomponga una metrica: dichiara solo quale metrica sta mostrando e
	 * quale totale, e il cablaggio fa il resto.
	 */
	onExplode?: (metricId: string, kpiTotal?: number | null) => void;
	/** Finestra temporale attiva; la scelta la fa la vista, il dato lo rifà il wiring. */
	period?: DashboardViewPeriod;
	onPeriodChange?: (period: DashboardViewPeriod) => void;
	/**
	 * Stato di caricamento per card: ognuna dipende da una fetch diversa e
	 * mostra lo skeleton finché quella specifica non è arrivata, senza
	 * aspettare le altre. Assente/`false` equivale a «dato pronto».
	 */
	loading?: DashboardViewLoading;
}

/** Vedi {@link DashboardPanelViewProps.loading}. */
export interface DashboardViewLoading {
	/** KPI del periodo e card «Andamento»: dipendono da `summary`/`config`. */
	kpis?: boolean;
	/** Card «Top Soci»: dipende da `summary` e `members`. */
	topMembers?: boolean;
	/** Card «Autoconsumo Configurazione»: dipende da `energyTotals`/`summary`. */
	autoconsumo?: boolean;
}

/**
 * Le finestre della dashboard. `ultima_validazione` è il mese chiuso: l'unico
 * su cui il GSE ha consolidato e i numeri non cambieranno più — per questo sta
 * accanto a «mese in corso» invece che dentro un archivio.
 */
export type DashboardViewPeriod = 'oggi' | 'ieri' | 'sette_giorni' | 'mese' | 'ultima_validazione';

const PERIOD_TABS: { value: DashboardViewPeriod; label: string }[] = [
	{ value: 'oggi', label: 'Oggi' },
	{ value: 'ieri', label: 'Ieri' },
	{ value: 'sette_giorni', label: 'Ultimi 7 gg' },
	{ value: 'mese', label: 'Mese in corso' },
	{ value: 'ultima_validazione', label: 'Ultima validazione' },
];

function memberLabel(m: EnergyMember): string {
	// Senza consenso il nome non arriva (INB-357): si dice, invece di un codice.
	if (m.anonimo) return 'Socio anonimo';
	const nome = m.nome || `Socio ${m.id.slice(0, 8)}`;
	return m.sonoIo ? `${nome} (tu)` : nome;
}

function memberInitials(m: EnergyMember): string {
	if (m.anonimo) return '··';
	const name = m.nome?.trim() || m.id.slice(0, 2);
	const parts = name.split(/\s+/);
	if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
	return name.slice(0, 2).toUpperCase();
}

const AVATAR_TONE_BY_TIPO: Record<string, WidgetTone> = {
	residential: 'accent',
	sme: 'success',
	public_administration: 'warn',
	ets: 'purple',
	office: 'accent',
	shop: 'success',
	factory: 'warn',
};

function tipoSocioLabel(tipo: string): string {
	return (MEMBER_TYPE_LABELS as Record<string, string>)[tipo] ?? tipo;
}

function fmtHour(value: string): string {
	return new Date(value).toLocaleTimeString('it-IT', {
		hour: '2-digit',
		minute: '2-digit',
	});
}

/** Percentuale intera e limitata a 0-100, per le barre dei KPI. */
function share(part: number, whole: number): number {
	if (whole <= 0) return 0;
	return Math.max(0, Math.min(100, Math.round((part / whole) * 100)));
}

/**
 * Etichette dell'asse X diradate secondo la durata della finestra: ore su uno o
 * due giorni, giorni della settimana su una settimana, numero del giorno sul
 * mese. La stringa vuota lascia il punto senza etichetta — è così che il mock
 * dirada le ore invece di ruotarle.
 */
function buildChartLabels(points: DashboardHourlyKpi[]): string[] {
	if (points.length === 0) return [];
	const dates = points.map((point) => new Date(point.hour));
	const spanDays = (dates[dates.length - 1].getTime() - dates[0].getTime()) / 86_400_000;

	if (spanDays <= 2) {
		return dates.map((date) =>
			date.getHours() % 3 === 0 ? String(date.getHours()).padStart(2, '0') : '',
		);
	}

	// Su più giorni l'etichetta cade sul primo punto di ogni giornata (o di una
	// giornata su tre quando sono troppe per starci tutte).
	const step = spanDays <= 8 ? 1 : 3;
	let lastDay = -1;
	let dayIndex = -1;
	return dates.map((date) => {
		const day = date.getDate();
		if (day === lastDay) return '';
		lastDay = day;
		dayIndex += 1;
		if (dayIndex % step !== 0) return '';
		return spanDays <= 8 ? date.toLocaleDateString('it-IT', { weekday: 'short' }) : String(day);
	});
}

/** Dati di una card della griglia «Panoramica» — le stesse quattro grandezze del pannello, per una configurazione sola. */
export interface DashboardConfigCardData {
	id: string;
	cerNome: string;
	configNome: string;
	/**
	 * Comune e area convenzionale della configurazione. Il mock mostra anche una
	 * sigla di provincia, che nello schema dati non esiste (solo `comune` e
	 * `areaConvenzionale`/GSE) — vedi `docs/mocks/slims-cer-v11-mock.md` §4 per
	 * lo stesso genere di adattamento mock→schema reale.
	 */
	comune?: string | null;
	areaCode?: string | null;
	prodKwh: number;
	condKwh: number;
	incKwh: number;
	/**
	 * Il feed del contatore manda letture (vedi `isFeedLive`). Il badge «LIVE»
	 * verde c'era sempre, anche senza nessuna lettura (INB-333).
	 */
	live: boolean;
}

export interface DashboardConfigCardProps {
	data: DashboardConfigCardData;
	onClick?: () => void;
}

function ConfigMetricCell({
	label,
	value,
	tone,
}: {
	label: string;
	value: string;
	tone: WidgetTone;
}) {
	return (
		<div className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]">
			<div className="mb-0.5 text-[9px] font-extrabold uppercase tracking-wide text-text-muted">
				{label}
			</div>
			<div className="font-mono text-sm font-extrabold" style={{ color: TONE_VAR[tone] }}>
				{value}
			</div>
		</div>
	);
}

/** Card compatta di una configurazione nella griglia «Panoramica»: le quattro grandezze del periodo, cliccabile per aprirne il pannello completo. */
export function DashboardConfigCard({ data, onClick }: DashboardConfigCardProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'w-full cursor-pointer rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left',
				'transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-accent-purple',
			)}
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{data.cerNome}
					</div>
					<div className="truncate text-sm font-extrabold leading-tight">{data.configNome}</div>
					<div className="mt-[3px] truncate text-[11px] text-text-muted">
						{data.comune}
						{data.areaCode && (
							<>
								{' · '}
								<span className="font-mono text-accent">{data.areaCode}</span>
							</>
						)}
					</div>
				</div>
				{data.live ? (
					<span className="inline-flex flex-none items-center gap-1.5">
						<span className="h-2 w-2 animate-pulse-dot rounded-full bg-accent-green" />
						<span className="font-mono text-[9px] font-extrabold text-accent-green">LIVE</span>
					</span>
				) : (
					<span
						className="inline-flex flex-none items-center gap-1.5"
						title="Nessuna lettura dal contatore nell'ultima ora"
					>
						<span className="h-2 w-2 rounded-full bg-text-muted" />
						<span className="font-mono text-[9px] font-extrabold text-text-muted">FEED FERMO</span>
					</span>
				)}
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				<ConfigMetricCell
					label="Energia Prodotta"
					value={`${fmtNum(data.prodKwh)} kWh`}
					tone="success"
				/>
				<ConfigMetricCell
					label="Energia Condivisa"
					value={`${fmtNum(data.condKwh)} kWh`}
					tone="purple"
				/>
				<ConfigMetricCell
					label="Energia Incentivabile"
					value={`${fmtNum(data.incKwh)} kWh`}
					tone="warn"
				/>
			</div>
			<Text as="span" variant="caption" weight="bold" tone="accent">
				clic → monitoraggio
			</Text>
		</button>
	);
}

export interface DashboardConfigCardSkeletonProps {
	/** Nome della CER — l'unico dato già noto (arriva da `useScopeTree`, non dal fetch di questa card): resta nitido, il resto sfoca finché comune/area code e le quattro metriche non arrivano. */
	cerNome: string;
}

/** Guscio di {@link DashboardConfigCard} in caricamento — stessa cornice/padding della card reale. */
export function DashboardConfigCardSkeleton({ cerNome }: DashboardConfigCardSkeletonProps) {
	return (
		<div
			className="w-full rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio di card generico (`div`), stesso pattern di `PanelCardSkeleton`.
			role="status"
			aria-label={`Caricamento configurazione - ${cerNome}`}
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{cerNome}
					</div>
					<BodySkeleton className="mb-1.5 h-4 w-32" />
					<BodySkeleton className="h-3 w-40" />
				</div>
				<BodySkeleton className="h-4 w-12 flex-none rounded-full" />
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={i}
						className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]"
					>
						<BodySkeleton className="mb-1 h-2.5 w-20" />
						<BodySkeleton className="h-4 w-16" />
					</div>
				))}
			</div>
			<BodySkeleton className="h-3 w-28" />
		</div>
	);
}

const DASHBOARD_VIEW_TABS: { value: DashboardViewMode; label: string }[] = [
	{ value: 'multi', label: 'Panoramica' },
	{ value: 'single', label: 'Configurazione singola' },
];

export function DashboardPageView({
	content,
	gridSlot,
	configs = [],
	view,
	onViewChange,
	selectedConfigId,
	onSelectConfig,
	multiPeriod,
	onMultiPeriodChange,
}: DashboardPageViewProps) {
	// Il toggle Panoramica/Configurazione-singola esiste solo se il wiring lo
	// alimenta: senza `view`/`onViewChange` la pagina mostra solo `content`,
	// come prima di questa vista (nessuna configurazione multipla da elencare).
	const hasToggle = configs.length > 1 && view != null && onViewChange != null;
	const selectedConfig = configs.find((c) => c.id === selectedConfigId) ?? null;
	const title =
		(!hasToggle || view === 'single') && selectedConfig ? selectedConfig.configNome : 'Dashboard';

	return (
		<div>
			<Row wrap justify="between" align="center" gap="lg" mb="sm">
				<PageTitle>{title}</PageTitle>
				{hasToggle && (
					// Stessa soluzione di `MonitoringView` per lo stesso problema: il
					// select va sulla propria riga sotto le schede, non affiancato —
					// `stretch` fa sì che le due righe abbiano la stessa larghezza.
					<Stack gap="sm" align="stretch">
						<SegmentedTabs
							items={DASHBOARD_VIEW_TABS}
							value={view}
							onChange={onViewChange}
							ariaLabel="Vista della dashboard"
							className="mb-0"
						/>
						{view === 'single' && configs.length > 0 && onSelectConfig && (
							<select
								className="sel min-w-[220px]"
								aria-label="Configurazione"
								value={selectedConfigId ?? ''}
								onChange={(e) => onSelectConfig(e.target.value)}
							>
								{configs.map((c) => (
									<option key={c.id} value={c.id}>
										{c.cerNome} · {c.configNome}
									</option>
								))}
							</select>
						)}
					</Stack>
				)}
			</Row>
			{hasToggle && view === 'multi' ? (
				<DashPanel>
					{onMultiPeriodChange && (
						<SegmentedTabs
							items={PERIOD_TABS}
							value={multiPeriod ?? 'oggi'}
							onChange={onMultiPeriodChange}
							ariaLabel="Periodo della panoramica"
							className="mb-4"
						/>
					)}
					<DashGrid layout="configs">{gridSlot}</DashGrid>
				</DashPanel>
			) : (
				content
			)}
		</div>
	);
}

export function DashboardPanelView({
	config,
	members,
	energyTotals,
	summary,
	aiActionsCount,
	showAiSuggestions = true,
	onExplode,
	period = 'oggi',
	onPeriodChange,
	loading,
}: DashboardPanelViewProps) {
	const membersById = useMemo(
		() => new Map(members.map((member) => [member.id, member])),
		[members],
	);
	const topMembers = useMemo(
		() =>
			// La quota di energia condivisa attribuita al socio nel mese in corso.
			// Prima i kWh erano produzione + consumo della finestra scelta (333 kWh
			// per un Comune che ne aveva prodotti 282, N-13, P-24).
			(summary?.members ?? [])
				.filter((kpi) => kpi.sharedMonthKwh > 0)
				.map((kpi) => {
					const member = membersById.get(kpi.memberId);
					return member ? { member, kpi } : null;
				})
				.filter((row): row is { member: EnergyMember; kpi: DashboardMemberKpi } => row !== null)
				.sort((a, b) => b.kpi.sharedMonthKwh - a.kpi.sharedMonthKwh)
				.slice(0, 5),
		[membersById, summary],
	);

	// Dati energetici reali dai misuratori GSE (riepilogo aggregato `energyTotals`).
	const hasRealData =
		energyTotals.totalImmessa > 0 ||
		energyTotals.totalPrelevata > 0 ||
		energyTotals.totalCondivisa > 0;
	const realCondivisaKwh = hasRealData ? energyTotals.totalCondivisa : 0;
	const realProdKwh = hasRealData ? energyTotals.totalImmessa : 0;
	const historicalAutoconsumo =
		hasRealData && realProdKwh > 0
			? Math.round(Math.min(100, (realCondivisaKwh / realProdKwh) * 100))
			: 0;

	// I KPI in cima sono grandezze **del periodo**.
	const periodTotals = summary?.period ?? null;
	const month = summary?.month ?? null;
	const periodProductionKwh = periodTotals?.productionKwh ?? 0;
	const periodConsumptionKwh = periodTotals?.consumptionKwh ?? 0;
	const periodSharedKwh = periodTotals?.sharedKwh ?? 0;
	const periodIncentivizedKwh = periodTotals?.incentivizedKwh ?? 0;
	// Quanta parte dei valori del periodo è stimata (INB-354). La condivisa è il
	// minimo fra produzione e consumo ora per ora: è stimata se lo è l'una o l'altro.
	const stimaProduzione = summary?.stima?.period.produzione ?? 0;
	const stimaCondivisa = Math.max(stimaProduzione, summary?.stima?.period.consumo ?? 0);
	const hasPeriodData =
		periodTotals !== null &&
		(periodProductionKwh > 0 || periodConsumptionKwh > 0 || periodSharedKwh > 0);
	const monthProductionKwh = month?.productionKwh ?? 0;
	const monthConsumptionKwh = month?.consumptionKwh ?? 0;
	const monthSharedKwh = month?.sharedKwh ?? 0;
	const hasMonthData =
		month !== null && (monthProductionKwh > 0 || monthConsumptionKwh > 0 || monthSharedKwh > 0);

	// La serie del grafico è la finestra scelta: `today` è già tagliato da
	// `dayFrom` a `asOf` lato server, quindi non c'è un secondo filtro qui.
	const chartPoints = useMemo(() => summary?.today ?? [], [summary]);
	const chartLabels = useMemo(() => buildChartLabels(chartPoints), [chartPoints]);
	const hasChartData = chartPoints.some(
		(point) => point.productionKwh > 0 || point.consumptionKwh > 0,
	);
	const peakIndex = useMemo(() => {
		if (chartPoints.length === 0) return undefined;
		let best = 0;
		for (let i = 1; i < chartPoints.length; i += 1) {
			if (chartPoints[i].productionKwh > chartPoints[best].productionKwh) best = i;
		}
		return chartPoints[best].productionKwh > 0 ? best : undefined;
	}, [chartPoints]);
	// Il titolo non varia più per periodo (v12): la differenza di finestra la
	// dice già `chartNote`, appena sotto.
	const chartTitle = 'Produzione vs Consumo';

	// v12 uniforma la didascalia a un solo formato per tutti i periodi — sparisce
	// la potenza istantanea di «oggi», il picco di «ieri», la percentuale di
	// copertura FV e la nota di consolidamento dell'ultima validazione.
	const periodLabel = PERIOD_TABS.find((tab) => tab.value === period)?.label ?? '';
	const chartNote = !hasChartData
		? 'Nessuna misura nell’intervallo selezionato'
		: `${periodLabel} · prodotti ${fmtNum(Math.round(periodProductionKwh))} kWh · consumati ${fmtNum(Math.round(periodConsumptionKwh))} kWh`;

	const selfConsumptionPct = periodTotals?.selfConsumptionPct ?? 0;

	const activeMembersCount = members.filter((m) => m.stato !== 'withdrawn').length;

	// ─── Calc traces ────────────────────────────────────────────────────────────
	const traceProduzione: CalcTraceData = {
		formula: 'E_prodotta [kWh] = Σh E_immessa_misuratori(h)',
		inputs: [
			{
				label: 'E immessa del periodo (SII)',
				value: hasPeriodData ? `${fmtNum(Math.round(periodProductionKwh))} kWh` : '-',
				source: 'real',
			},
			{
				label: 'E immessa totale (SII)',
				value: hasRealData ? `${fmtNum(Math.round(realProdKwh))} kWh` : '-',
				source: 'real',
			},
			{ label: 'Potenza installata', value: `${config?.kwp ?? 0} kWp`, source: 'config' },
		],
		norm: 'ARERA 727/2022/R/eel (TIAD) - dati SII Acquirente Unico',
		note: hasPeriodData
			? stimaNote(stimaProduzione)
			: 'Dati non ancora disponibili - in attesa connessione misuratori SII.',
	};

	const traceCondivisa: CalcTraceData = {
		formula: 'E_ACI [kWh] = Σh min(E_immessa_h, E_prelevata_h)',
		inputs: [
			{
				label: 'E condivisa del periodo (SII)',
				value: hasPeriodData ? `${fmtNum(Math.round(periodSharedKwh))} kWh` : '-',
				source: 'real',
			},
			{
				label: 'E prelevata del periodo (SII)',
				value: hasPeriodData ? `${fmtNum(Math.round(periodConsumptionKwh))} kWh` : '-',
				source: 'real',
			},
			{
				label: 'Autoconsumo storico',
				value: hasRealData ? fmtPct(historicalAutoconsumo) : '-',
				source: 'calc',
			},
		],
		norm: 'Art. 3 c.1 D.M. 414/2023 + ARERA 727/2022 - formula oraria min(im,prel)',
		note: withStima(
			'Il calcolo è orario sui misuratori SII; il KPI è la somma delle ore della finestra scelta.',
			stimaCondivisa,
		),
	};

	const traceIncentivabile: CalcTraceData = {
		formula: 'E_incentivabile = E_ACI × (1 - quota_preesistente)',
		inputs: [
			{
				label: 'E condivisa del periodo',
				value: hasPeriodData ? `${fmtNum(Math.round(periodSharedKwh))} kWh` : '-',
				source: 'real',
			},
			{
				label: 'Quota impianti preesistenti',
				value: `${config?.preEsistentePct ?? 0}% (cap 30%)`,
				source: 'config',
			},
			{
				label: 'E entro soglia GSE',
				value: hasPeriodData ? `${fmtNum(periodIncentivizedKwh)} kWh` : '-',
				source: 'calc',
			},
		],
		norm: 'D.M. 414/2023 art. 7 - quota da impianti preesistenti non incentivabile',
		note: withStima(
			'La quota preesistente si misura sui misuratori marcati come tali; in loro assenza vale il valore di configurazione.',
			stimaCondivisa,
		),
	};

	const traceAutoconsumo: CalcTraceData = {
		formula: 'AC% = E_ACI ÷ E_immessa × 100',
		inputs: [
			{
				label: 'E condivisa (SII)',
				value: hasPeriodData ? `${fmtNum(Math.round(periodSharedKwh))} kWh` : '-',
				source: 'real',
			},
			{
				label: 'E immessa (SII)',
				value: hasPeriodData ? `${fmtNum(Math.round(periodProductionKwh))} kWh` : '-',
				source: 'real',
			},
			{
				label: 'E prelevata (SII)',
				value: hasPeriodData ? `${fmtNum(Math.round(periodConsumptionKwh))} kWh` : '-',
				source: 'real',
			},
		],
		norm: 'D.M. 414/2023 Art. 3 + ARERA 727/2022 - definizione autoconsumo collettivo',
		note: 'Valori della finestra scelta, aggiornati quando arriva una nuova lettura.',
	};

	return (
		<DashPanel>
			{onPeriodChange && (
				<SegmentedTabs
					items={PERIOD_TABS}
					value={period}
					onChange={onPeriodChange}
					ariaLabel="Periodo dei dati"
				/>
			)}

			{/* ─── KPI del periodo ─────────────────────────────────────────────── */}
			<DashSection ariaLabel="KPI del periodo">
				<DashGrid layout="kpi">
					{loading?.kpis ? (
						<>
							<PanelCardSkeleton title="Energia Prodotta">
								<KpiSkeleton />
							</PanelCardSkeleton>
							<PanelCardSkeleton title="Energia Condivisa">
								<KpiSkeleton />
							</PanelCardSkeleton>
							<PanelCardSkeleton title="Energia Incentivabile">
								<KpiSkeleton />
							</PanelCardSkeleton>
						</>
					) : (
						<>
							<PanelCard
								title="Energia Prodotta"
								aside={
									<Row gap="sm">
										<CalcTrace data={traceProduzione} />
										{onExplode && (
											<ExplodeButton
												label="Esplodi l'energia prodotta per POD"
												onClick={() => onExplode('produzione_mensile', periodProductionKwh)}
												disabled={!hasPeriodData}
												disabledReason="Nessuna misura di produzione nella finestra scelta."
											/>
										)}
									</Row>
								}
							>
								<KpiBig
									value={hasPeriodData ? fmtNum(Math.round(periodProductionKwh)) : '-'}
									tone="success"
									unit="kWh"
									bar={{ pct: 100, label: 'Riferimento: tutta la produzione del periodo' }}
								/>
							</PanelCard>

							<PanelCard
								title="Energia Condivisa"
								aside={
									<Row gap="sm">
										<CalcTrace data={traceCondivisa} />
										{onExplode && (
											<ExplodeButton
												label="Esplodi l'energia condivisa per POD"
												onClick={() => onExplode('energia_condivisa', periodSharedKwh)}
												disabled={!hasPeriodData}
												disabledReason="Nessuna energia condivisa nella finestra scelta."
											/>
										)}
									</Row>
								}
							>
								<KpiBig
									value={hasPeriodData ? fmtNum(Math.round(periodSharedKwh)) : '-'}
									tone="purple"
									unit="kWh"
									bar={{
										pct: share(periodSharedKwh, periodProductionKwh),
										label: 'Quota condivisa sulla produzione del periodo',
									}}
								/>
							</PanelCard>

							<PanelCard
								title="Energia Incentivabile"
								aside={
									<Row gap="sm">
										<CalcTrace data={traceIncentivabile} />
										{/* Disattivato per costruzione, non per mancanza di dati: la quota
								    preesistente si toglie dal totale di comunità e non ha un
								    corrispondente per POD. Il bottone resta visibile perché
								    l'esploso c'è su tutte le altre card della riga. */}
										{onExplode && (
											<ExplodeButton
												label="Esplodi l'energia incentivabile per POD"
												onClick={() => onExplode('energia_incentivabile', periodIncentivizedKwh)}
												disabled
												disabledReason="La quota da impianti preesistenti si applica al totale di comunità: esplodi l'energia condivisa."
											/>
										)}
									</Row>
								}
							>
								<KpiBig
									value={hasPeriodData ? fmtNum(periodIncentivizedKwh) : '-'}
									tone="warn"
									unit="kWh"
									bar={{
										pct: share(periodIncentivizedKwh, periodProductionKwh),
										label: 'Quota incentivabile sulla produzione del periodo',
									}}
								/>
							</PanelCard>
						</>
					)}
				</DashGrid>
			</DashSection>

			{/* ─── Andamento del periodo ───────────────────────────────────────── */}
			<DashSection ariaLabel="Andamento del periodo">
				<DashGrid layout="full">
					{loading?.kpis ? (
						<PanelCardSkeleton title={chartTitle}>
							<ChartSkeleton height={360} />
						</PanelCardSkeleton>
					) : (
						<PanelCard
							title={chartTitle}
							aside={
								<Row gap="sm">
									{onExplode && (
										<ExplodeButton
											label="Esplodi la curva produzione/consumo per POD"
											onClick={() => onExplode('curva_produzione_consumo', null)}
											disabled={!hasChartData}
											disabledReason="Nessun punto della curva nella finestra scelta."
										/>
									)}
									<CalcTrace
										data={{
											formula:
												'P_prod(h) [kW] = Σ E_immessa_misuratori(h) [kWh] ÷ 1h\nP_cons(h) [kW] = Σ E_prelevata_misuratori(h) [kWh] ÷ 1h',
											inputs: [
												{
													label: 'Punti orari della finestra',
													value: `${chartPoints.length}`,
													source: 'real',
												},
												{
													label: 'Ultimo aggiornamento',
													value: summary?.asOf ? fmtHour(summary.asOf) : '-',
													source: 'real',
												},
												{
													label: 'Riconciliazione',
													value: 'GSE → bolletta → telemetria → stima',
													source: 'norm',
												},
											],
											norm: 'ARERA 727/2022/R/eel (TIAD) - aggregazione oraria',
											note: 'La curva si aggiorna automaticamente quando arrivano nuove letture energetiche.',
										}}
									/>
								</Row>
							}
						>
							<Text as="div" variant="caption" tone="muted" className="mb-2.5">
								{chartNote}
							</Text>
							<SlChart
								series={[
									{
										name: 'Produzione',
										color: 'var(--color-series-2)',
										vals: chartPoints.map((point) => point.productionKwh),
									},
									{
										name: 'Consumo',
										color: 'var(--color-series-1)',
										vals: chartPoints.map((point) => point.consumptionKwh),
									},
								]}
								x={chartLabels}
								hoverX={(i) =>
									chartPoints[i]
										? new Date(chartPoints[i].hour).toLocaleString('it-IT', {
												day: '2-digit',
												month: '2-digit',
												hour: '2-digit',
												minute: '2-digit',
											})
										: ''
								}
								unit="kW"
								height={360}
								peak={peakIndex}
								ariaLabel="Produzione e consumo della finestra selezionata"
								emptyMessage="Nessun dato energetico nell’intervallo selezionato"
							/>
						</PanelCard>
					)}
				</DashGrid>
			</DashSection>

			{/* ─── Soci e autoconsumo ─────────────────────────────────────────── */}
			<DashSection ariaLabel="Soci e gestione">
				<DashGrid layout="bottomPair">
					{loading?.topMembers ? (
						<PanelCardSkeleton title="Top Soci · mese in corso">
							<ListSkeleton rows={5} />
						</PanelCardSkeleton>
					) : (
						<PanelCard
							title="Top Soci · mese in corso"
							aside={
								<Row gap="sm">
									<CalcTrace
										data={{
											formula:
												'Rank = E_condivisa_socio_mese = E_condivisa_mese × prelievo_socio / Σ prelievi',
											inputs: [
												{
													label: 'Energia condivisa mese',
													value: hasMonthData ? `${fmtNum(Math.round(monthSharedKwh))} kWh` : '-',
													source: 'real',
												},
												{
													label: 'Numero soci top 5',
													value: `${topMembers.length}`,
													source: 'real',
												},
											],
											norm: 'TIAD ARERA 727/2022 - misure riconciliate per POD',
											note: 'I kWh sono del mese in corso, qualunque finestra sia scelta sopra: la quota di energia condivisa attribuita al socio in proporzione al suo prelievo.',
										}}
									/>
								</Row>
							}
						>
							{topMembers.length === 0 ? (
								<StatusBlock
									kind="empty"
									size="sm"
									message={
										activeMembersCount > 0
											? 'Nessun dato energetico disponibile per il periodo'
											: 'Nessun socio registrato'
									}
								/>
							) : (
								<div>
									{topMembers.map(({ member, kpi }) => {
										const activityKwh = Math.round(kpi.sharedMonthKwh);
										return (
											<MemberRowItem
												key={member.id}
												initials={memberInitials(member)}
												avatarTone={AVATAR_TONE_BY_TIPO[member.tipo ?? ''] ?? 'accent'}
												title={memberLabel(member)}
												subtitle={tipoSocioLabel(member.tipo ?? '')}
												kwhLabel={`${fmtNum(activityKwh)} kWh`}
											/>
										);
									})}
								</div>
							)}
						</PanelCard>
					)}

					{loading?.autoconsumo ? (
						<PanelCardSkeleton title="Autoconsumo Configurazione">
							<RingSkeleton lines={3} />
						</PanelCardSkeleton>
					) : (
						<PanelCard
							title="Autoconsumo Configurazione"
							aside={
								<Row gap="sm">
									<CalcTrace data={traceAutoconsumo} />
									{onExplode && (
										<ExplodeButton
											label="Esplodi l'autoconsumo per POD"
											onClick={() => onExplode('autoconsumo_pct', selfConsumptionPct)}
											disabled={!hasPeriodData}
											disabledReason="Nessuna misura nella finestra scelta."
										/>
									)}
								</Row>
							}
						>
							<RingGauge percentage={selfConsumptionPct} size={168} caption={false} />
							<BalanceList>
								<BalanceLine
									label="Energia prodotta"
									value={hasPeriodData ? fmtNum(Math.round(periodProductionKwh)) : '-'}
									unit="kWh"
								/>
								<BalanceLine
									label="Energia condivisa"
									value={hasPeriodData ? fmtNum(Math.round(periodSharedKwh)) : '-'}
									unit="kWh"
								/>
								<BalanceLine
									label="Energia prelevata"
									value={hasPeriodData ? fmtNum(Math.round(periodConsumptionKwh)) : '-'}
									unit="kWh"
								/>
								{showAiSuggestions && (
									<div className="mt-2.5">
										{typeof aiActionsCount === 'number' && aiActionsCount > 0
											? `AI: ${aiActionsCount} ${aiActionsCount === 1 ? 'carico ottimizzato' : 'carichi ottimizzati'} questo mese`
											: 'AI: nessun carico ottimizzato questo mese'}
									</div>
								)}
								<div>
									{/* Il mock tinge questa riga sempre di verde (`var(--accent3)`),
									    sia "raggiungibile con AI" che "✓ Target raggiunto!" — mai un
									    terzo colore per "non ancora". Qui il numero è reale (non il
									    testo fisso del mock), ma il tono resta verde con dato, non
									    accent/blu. */}
									Target 80%:{' '}
									<Text as="span" variant="inherit" tone={hasPeriodData ? 'success' : 'muted'}>
										{hasPeriodData ? `${selfConsumptionPct}% / 80%` : '-'}
									</Text>
								</div>
							</BalanceList>
						</PanelCard>
					)}
				</DashGrid>
			</DashSection>
		</DashPanel>
	);
}
